import { useEffect, useState, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import Typography from '@mui/joy/Typography';
import Stack from '@mui/joy/Stack';
import Chip from '@mui/joy/Chip';
import Tooltip from '@mui/joy/Tooltip';

// import copy from 'copy-to-clipboard';


import { CallParticipant } from '../stores/call_participant_store';


import { Call } from 'stores/call_store';

import usePhone from 'hooks/usePhone';
import useUserIdentity from 'hooks/useUserIdentity';
import IdentityAvatar from 'components/IdentityAvatar';
import { TimeUI } from 'components/Date';
import { pluralize, getIcon, phoneFormat } from 'utils/misc';
import { Success } from 'components/FancyToast';

export const ParticipantsItem = ({ participant, showControls = false }: { participant: CallParticipant, showControls: boolean }) => {
    const PhoneIcon = getIcon('LocalPhone');
    const PhoneHangupIcon = getIcon('PhoneDisabled');
    const [confirmingHangup, setConfirmingHangup] = useState(false);
    const [ended, setEnded] = useState(false);

    const { phoneRemoveParticipant } = usePhone();

    function getColor(status: string | null) {
        if(ended) {
            return 'neutral';
        }
        if (status == 'in-progress') {
            return 'success';
        } else if (status == 'initiated') {
            return 'primary';
        } else if (status == 'queued') {
            return 'warning';
        }
        else {
            return 'neutral';
        }
    }

    function doRemoveParticipant(participant: CallParticipant) {
        // do this with they key?
        phoneRemoveParticipant(participant.call, participant.twilio_call_sid).then(() => {
            setEnded(true);
        });
    }

    const color = getColor(participant.twilio_status);
    const shouldShowControls = showControls &&
        ['in-progress', 'queued', 'ringing', 'initiated'].includes(participant.twilio_status);

    return (
        <Stack
            key={participant.key}
            alignItems="center"
            mb={2}>

            <Typography level="body-sm">
                <Tooltip arrow title="Copy number to clipboard">
                    <Chip
                        startDecorator={<PhoneIcon />}
                        variant="outlined"
                        onClick={() => {
                            navigator.clipboard.writeText(participant.phone_number)
                            Success("Copied phone number to clipboard");
                        }}
                        color={color}>
                        {phoneFormat(participant.phone_number)}
                    </Chip>
                </Tooltip>
            </Typography>

            {!ended && shouldShowControls &&
                <Stack>
                    {confirmingHangup ?
                        <Stack spacing={1}>
                            <Typography level="body-xs">Are you sure?</Typography>
                            <Chip
                                endDecorator={<PhoneHangupIcon />}
                                color="warning"
                                onClick={() => { doRemoveParticipant(participant); setConfirmingHangup(false); }}>Yes, remove
                            </Chip>
                            <Chip
                                endDecorator={<PhoneHangupIcon />}
                                variant="plain"
                                color="neutral"
                                onClick={() => { setConfirmingHangup(false) }}>Nevermind
                            </Chip>
                        </Stack>

                        :
                        <Tooltip arrow title="Remove this caller from the call">
                            <Chip
                                variant="plain"
                                endDecorator={<PhoneHangupIcon />}
                                onClick={() => { setConfirmingHangup(true) }}>
                                Remove
                            </Chip>
                        </Tooltip>
                    }
                </Stack>
            }

        </Stack>
    );
};

export const ParticipantsList = ({ participants, showControls = false }: { participants: CallParticipant[], showControls: boolean }) => {
    return (
        <Stack>
            {participants.map((participant) =>
                <ParticipantsItem key={participant.key} participant={participant} showControls={showControls} /> )}
        </Stack>
    );
};


const CollabCallItem = observer(({ call, expanded = false, showControls = false }: { call: Call, expanded?: boolean, showControls?: boolean }) => {

    const { fetchCallParticipants, getCallParticipants } = usePhone();
    const { currentIdentity } = useUserIdentity();
    const [loaded, setLoaded] = useState(false);
    const participants = getCallParticipants(call?.key);
    const DownIcon = getIcon('ArrowDropDown');
    const forceExpanded = (expanded || (participants.length < 2));
    const [showParticipants, setShowParticipants] = useState(forceExpanded);

    const loader = useCallback(() => {
        if (call?.key && currentIdentity?.key) {
            setLoaded(true);
            fetchCallParticipants(call?.key, currentIdentity?.key);
        }
    }, [call?.key, currentIdentity?.key, setLoaded, fetchCallParticipants]);

    useEffect(() => {
        if (!loaded) {
            loader();
        }
    }, [loader, loaded, setLoaded]);

    if (!call)
        return null;

    return (

        <Stack direction="row">

            <Stack flexGrow={1}>

                <Typography level="body-sm">
                    <TimeUI ts={call?.date_created} />
                    <IdentityAvatar interactive={false} size="name" identityKey={call.identity} />
                </Typography>
            </Stack>

            <Stack>
                {!forceExpanded &&
                    <Chip variant="plain"
                        color="neutral"
                        endDecorator={!showParticipants ?
                            <DownIcon /> :
                            <DownIcon sx={{ transform: 'rotate(180deg)' }} />}
                        onClick={() => { setShowParticipants(!showParticipants) }}>
                        {pluralize(participants?.length, ' participant')}
                    </Chip>}

                {showParticipants &&
                    <ParticipantsList showControls={showControls} participants={participants} />}
            </Stack>


        </Stack>
    );
});

export default CollabCallItem;
